<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

export default {
  name: 'CreateTicketModal',
  props: {
    messageId: {
      type: Number,
      required: false,
      default: null,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    messageContent: {
      type: String,
      default: '',
    },
    onClose: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      title: '',
      description: '',
      status: 'open',
      urgency: 'Baixa',
      category: '',
      service: '',
      dueDate: '',
      assigneeId: null,
      teamId: null,
      isSubmitting: false,
      isFetchingAi: false,
    };
  },
  computed: {
    ...mapGetters({
      agents: 'agents/getAgents',
      teams: 'teams/getTeams',
      currentUser: 'getCurrentUser',
      currentAccountId: 'getCurrentAccountId',
      getAccount: 'accounts/getAccount',
    }),
    currentAccountObj() {
      return this.getAccount(this.currentAccountId);
    },
    ticketServices() {
      return this.currentAccountObj?.custom_attributes?.ticket_services || [];
    },
    ticketCategories() {
      return this.currentAccountObj?.custom_attributes?.ticket_categories || [];
    },
    ticketUrgencies() {
      const urgencies =
        this.currentAccountObj?.custom_attributes?.ticket_urgencies;
      if (Array.isArray(urgencies) && urgencies.length > 0) return urgencies;
      return [
        {
          name: 'Baixa',
          label: this.$t('TICKET_DETAILS.URGENCY_LOW'),
          color: '#3b82f6',
        },
        {
          name: 'Média',
          label: this.$t('TICKET_DETAILS.URGENCY_MEDIUM'),
          color: '#eab308',
        },
        {
          name: 'Alta',
          label: this.$t('TICKET_DETAILS.URGENCY_HIGH'),
          color: '#f97316',
        },
        {
          name: 'Urgente',
          label: this.$t('TICKET_DETAILS.URGENCY_URGENT'),
          color: '#ef4444',
        },
      ];
    },
    hasAiFeature() {
      // Ticket summary generation is available whenever the account has the
      // LumerAI module enabled. Some plans expose the module as `ia` without
      // exposing the legacy `lumerai_summaries` feature flag.
      return (
        this.$store.getters['accounts/isFeatureEnabledonAccount'](
          this.currentAccountId,
          FEATURE_FLAGS.LUMERAI_SUMMARIES
        ) ||
        this.$store.getters['accounts/isFeatureEnabledonAccount'](
          this.currentAccountId,
          FEATURE_FLAGS.LUMERAI
        ) ||
        this.currentAccountObj?.active_modules?.ia === true ||
        this.currentAccountObj?.active_modules?.ia === 'true'
      );
    },
    hasExistingTicket() {
      const chat = this.$store.getters.getSelectedChat;
      return !!chat?.ticket;
    },
    isAdmin() {
      return this.currentUser.role === 'administrator';
    },
    assignableAgents() {
      // Regra: Somente abre com o proprio nome se nao for admin
      if (this.currentUser.role === 'administrator') {
        return this.agents;
      }
      return this.agents.filter(a => a.id === this.currentUser.id);
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('teams/get');
    // Preenche com o prório usuário se não for admin
    if (this.currentUser.role !== 'administrator') {
      this.assigneeId = this.currentUser.id;
    }
  },
  methods: {
    assignToMe() {
      this.assigneeId = this.currentUser.id;
    },
    assignToMyTeam() {
      // Pega o primeiro time em que o usuário logado está presente (se aplicável na store, mas se não, pega o primeiro time disponível ou se o currentUser tiver um account_id)
      // Como a store de teams geralmente tem os times da conta, vamos pegar o primeiro. Para ser exato, precisaríamos da lista de times do usuário.
      if (this.teams && this.teams.length > 0) {
        this.teamId = this.teams[0].id;
      }
    },
    async fetchAiSuggestion() {
      this.isFetchingAi = true;
      try {
        const response = await window.axios.get(
          `/api/v1/accounts/${this.currentAccountId}/conversations/${this.conversationId}/ticket_summary`
        );
        if (response.data) {
          this.title = response.data.titulo || '';
          this.description = response.data.resumo || '';
          useAlert(this.$t('TICKETS.ALERTS.SUGGESTION_APPLIED'));
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.error ||
          this.$t('TICKETS.ALERTS.SUGGESTION_ERROR');
        useAlert(errorMessage);
      } finally {
        this.isFetchingAi = false;
      }
    },
    async submitTicket() {
      if (!this.title.trim()) {
        useAlert(this.$t('TICKETS.ALERTS.TITLE_REQUIRED'));
        return;
      }
      this.isSubmitting = true;
      try {
        const response = await this.$store.dispatch('tickets/create', {
          conversation_id: this.conversationId,
          message_id: this.messageId,
          title: this.title,
          description: this.description,
          status: this.status,
          urgency: this.urgency,
          category: this.category,
          service: this.service,
          due_date: this.dueDate,
          assignee_id: this.assigneeId,
          team_id: this.teamId,
        });

        await this.$store.dispatch('getConversation', this.conversationId);

        this.$store.commit('UPDATE_CONVERSATION', {
          id: this.conversationId,
          ticket: response,
        });

        useAlert(this.$t('TICKETS.ALERTS.CREATED'));
        this.onClose();
      } catch (error) {
        useAlert(this.$t('TICKETS.ALERTS.CREATE_ERROR'));
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-full w-full mx-auto">
    <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
      <h2 class="text-lg font-bold text-slate-800 dark:text-slate-200">
        {{ $t('CREATE_TICKET.FROM_MESSAGE') }}
      </h2>
    </div>

    <div class="p-6 flex-1 overflow-y-auto">
      <div v-if="hasAiFeature" class="mb-4">
        <button
          type="button"
          :disabled="isFetchingAi"
          class="flex items-center text-sm font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-3 py-1.5 rounded hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors disabled:opacity-50"
          @click="fetchAiSuggestion"
        >
          <span class="i-lucide-sparkles mr-1.5 w-4 h-4" />
          {{
            isFetchingAi
              ? $t('CREATE_TICKET.AI_LOADING')
              : $t('CREATE_TICKET.FILL_WITH_AI')
          }}
        </button>
      </div>

      <div
        v-if="messageContent"
        class="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/50 rounded-lg"
      >
        <p
          class="text-xs text-blue-600 dark:text-blue-400 font-semibold mb-2 uppercase tracking-wider"
        >
          {{ $t('CREATE_TICKET.MESSAGE_CONTEXT') }}
        </p>
        <p class="text-sm text-slate-700 dark:text-slate-300 italic">
          {{ $t('CREATE_TICKET.QUOTE_START') }}{{ messageContent
          }}{{ $t('CREATE_TICKET.QUOTE_END') }}
        </p>
      </div>

      <div
        v-if="hasExistingTicket"
        class="p-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/50 rounded-lg text-center"
      >
        <span
          class="i-lucide-alert-triangle w-8 h-8 text-amber-500 mx-auto mb-2 block"
        />
        <h3 class="text-lg font-medium text-amber-800 dark:text-amber-200 mb-1">
          {{ $t('CREATE_TICKET.ATTENTION') }}
        </h3>
        <p class="text-sm text-amber-700 dark:text-amber-300">
          {{ $t('CREATE_TICKET.EXISTING_TICKET') }}
        </p>
      </div>

      <form v-else class="space-y-4" @submit.prevent="submitTicket">
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ $t('CREATE_TICKET.TITLE') }}
          </label>
          <input
            v-model="title"
            type="text"
            required
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-slate-100"
            :placeholder="$t('CREATE_TICKET.TITLE_PLACEHOLDER')"
          />
        </div>

        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ $t('CREATE_TICKET.DESCRIPTION') }}
          </label>
          <textarea
            v-model="description"
            rows="4"
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-slate-100"
            :placeholder="$t('CREATE_TICKET.DESCRIPTION_PLACEHOLDER')"
          />
        </div>

        <div class="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ $t('CREATE_TICKET.SERVICE') }}
            </label>
            <div class="relative w-full">
              <select
                v-model="service"
                class="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-slate-100 appearance-none !bg-none"
              >
                <option value="">{{ $t('CREATE_TICKET.SELECT') }}</option>
                <option
                  v-for="svc in ticketServices"
                  :key="svc.name"
                  :value="svc.name"
                >
                  {{ svc.name }}
                </option>
              </select>
              <div
                class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ $t('CREATE_TICKET.CATEGORY') }}
            </label>
            <div class="relative w-full">
              <select
                v-model="category"
                class="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-slate-100 appearance-none !bg-none"
              >
                <option value="">{{ $t('CREATE_TICKET.SELECT') }}</option>
                <option
                  v-for="cat in ticketCategories"
                  :key="cat.name"
                  :value="cat.name"
                >
                  {{ cat.name }}
                </option>
              </select>
              <div
                class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div class="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ $t('CREATE_TICKET.STATUS') }}
            </label>
            <div class="relative w-full">
              <select
                v-model="status"
                class="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-slate-100 appearance-none !bg-none"
              >
                <option value="open">{{ $t('CREATE_TICKET.OPEN') }}</option>
                <option value="in_progress">
                  {{ $t('CREATE_TICKET.IN_PROGRESS') }}
                </option>
                <option value="pending">
                  {{ $t('CREATE_TICKET.PENDING') }}
                </option>
                <option value="resolved">
                  {{ $t('CREATE_TICKET.RESOLVED') }}
                </option>
                <option value="closed">{{ $t('CREATE_TICKET.CLOSED') }}</option>
              </select>
              <div
                class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ $t('CREATE_TICKET.URGENCY') }}
            </label>
            <div class="relative w-full">
              <select
                v-model="urgency"
                class="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-slate-100 appearance-none !bg-none"
              >
                <option
                  v-for="urg in ticketUrgencies"
                  :key="urg.name"
                  :value="urg.name"
                >
                  {{ urg.label || urg.name }}
                </option>
              </select>
              <div
                class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div class="mb-4">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ $t('CREATE_TICKET.DUE_DATE') }}
          </label>
          <input
            v-model="dueDate"
            type="datetime-local"
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-slate-100"
          />
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <div class="flex items-center justify-between mb-1">
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                {{ $t('CREATE_TICKET.ASSIGNEE') }}
              </label>
              <button
                v-if="isAdmin"
                type="button"
                class="text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 focus:outline-none"
                :title="$t('CREATE_TICKET.ASSIGN_TO_ME')"
                @click="assignToMe"
              >
                <span class="i-lucide-user h-4 w-4" />
              </button>
            </div>

            <div v-if="isAdmin" class="relative w-full">
              <select
                v-model="assigneeId"
                class="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-slate-100 appearance-none !bg-none"
              >
                <option :value="null">{{ $t('CREATE_TICKET.NONE') }}</option>
                <option
                  v-for="agent in assignableAgents"
                  :key="agent.id"
                  :value="agent.id"
                >
                  {{ agent.name }}
                </option>
              </select>
              <div
                class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            <div
              v-else
              class="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-md bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-sm flex items-center"
            >
              <span class="i-lucide-user h-4 w-4 mr-2" />
              {{ currentUser.name }}
            </div>
          </div>
          <div>
            <div class="flex items-center justify-between mb-1">
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                {{ $t('CREATE_TICKET.TEAM') }}
              </label>
              <button
                type="button"
                class="text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 focus:outline-none"
                :title="$t('CREATE_TICKET.ASSIGN_TO_TEAM')"
                @click="assignToMyTeam"
              >
                <span class="i-lucide-users h-4 w-4" />
              </button>
            </div>
            <div class="relative w-full">
              <select
                v-model="teamId"
                class="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-slate-100 appearance-none !bg-none"
              >
                <option :value="null">{{ $t('CREATE_TICKET.NONE') }}</option>
                <option v-for="team in teams" :key="team.id" :value="team.id">
                  {{ team.name }}
                </option>
              </select>
              <div
                class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>

    <div class="px-6 py-4 flex justify-end space-x-3 mt-4">
      <button
        class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm"
        @click="onClose"
      >
        {{ $t('CREATE_TICKET.CANCEL') }}
      </button>
      <button
        v-if="!hasExistingTicket"
        :disabled="isSubmitting"
        class="px-4 py-2 text-sm font-medium text-white bg-woot-500 border border-transparent rounded-md hover:bg-woot-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-woot-500 shadow-sm disabled:opacity-50"
        @click="submitTicket"
      >
        {{
          isSubmitting
            ? $t('CREATE_TICKET.CREATING')
            : $t('CREATE_TICKET.CREATE')
        }}
      </button>
    </div>
  </div>
</template>
